import {
    ActionIcon,
    Alert,
    Button,
    Card,
    Center,
    Drawer,
    Group,
    Loader,
    Modal,
    Pagination,
    SegmentedControl,
    Stack,
    Tabs,
    Text,
    TextInput,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { brand } from '../../branding';
import { useAssetUrl } from '../blobAssets';
import {
    applyColumnFilters,
    applyFilters,
    buildCategoryLabels,
    buildDocumentUsageIndex,
    buildIndexByArtNr,
    buildTreeIndex,
    byKey,
    computeFacets,
    groupMediaByDocument,
    scopeDocumentsToSystem,
    selectDocuments,
    sortItems,
    type DocumentSortKey,
    type DocumentSortState,
    type SortState,
} from '../engine';
import { useCatalogLabels } from '../labels';
import { DATA_LANGUAGE, useCatalogConfig, useCatalogIndex, useDocumentIndex, useLabels, useSystem, useTree } from '../queries';
import { filtersAtom, listColumnsAtom } from '../state';
import { loc, nodeLabel, type ActiveFilters, type ComputedFacet, type IndexItem, type ListColumn } from '../types';
import { ActiveFilterChips, columnFilterChips, facetFilterChips, filterChipsWithPresets } from './ActiveFilterChips';
import { ColumnPicker } from './ColumnPicker';
import { DocumentFilterBar } from './DocumentFilterBar';
import { DocumentSearchResults } from './DocumentSearchResults';
import { FacetsPanel } from './FacetsPanel';
import { FILTER_DRAWER_HEADER_PROPS, FilterDrawerHeader } from './FilterDrawerHeader';
import { MOBILE_H1_STYLE, useIsMobile } from './mobileLayout';
import { ProductGrid } from './ProductGrid';
import { ProductImage } from './ProductImage';
import { RichText } from './RichText';
import { ViewModeToggle } from './ViewModeToggle';

// Matches selectDocuments'/CatalogPage's own fallback minChars value.
const SEARCH_MIN_CHARS = 2;
const DOCUMENTS_PER_PAGE = 20;

interface SystemDetailContentProps {
    nodeId: string;
    /** Jumps to a different tree node - used by the Dokumente tab's usage links (a document may
     * be assigned to a different system than the one currently shown). Embedded in CatalogPage's
     * own content pane, so this is just CatalogPage's own handleSelectCategory. */
    onNavigateToNode: (nodeId: string) => void;
}

/** What the Dokumente tab shows: the whole union, only direct assignments, or only the articles' ones. */
type SystemDocumentScope = 'all' | 'system' | 'articles';

/**
 * The three-tab "System" view (Systeminformationen/Artikelliste/Dokumente) for a tree leaf node -
 * rendered directly inside CatalogPage's content pane (same place the product grid otherwise
 * occupies) so the category tree sidebar stays visible instead of navigating to a separate page
 * (see chat: "Kann man das System so einbetten, dass es im Content erscheint und links die
 * Navigation erhalten bleibt?"). The Dokumente tab shows the UNION of documents assigned directly
 * to this system node AND documents assigned to any of its own articles - the legacy application
 * shows both (e.g. an anchor's own datasheet appears under the system it belongs to), whereas
 * documents.json alone only carries the direct-to-node assignments.
 */
export const SystemDetailContent = ({ nodeId, onNavigateToNode }: SystemDetailContentProps) => {
    const isMobile = useIsMobile();
    const { locale, L } = useCatalogLabels();

    const systemQuery = useSystem(nodeId);
    const treeQuery = useTree();
    const indexQuery = useCatalogIndex(DATA_LANGUAGE);
    const configQuery = useCatalogConfig();
    // LOV code -> display text for the facet panel below (same source and same empty fallback as
    // CatalogPage's own).
    const labelsQuery = useLabels(locale);
    // Force-enabled: this view needs documents.json regardless of CatalogPage's own
    // "products vs. documents" search-mode toggle (searchModeAtom), which this view never shows.
    const documentIndexQuery = useDocumentIndex(true);
    // Whatever facet filters are currently active in CatalogPage (including a landing-page preset
    // like "Weltweites Sortiment") - keeps this tab's article count consistent with the navigation
    // tree's own badge for the same node. They used to act here invisibly: shown as chips only up
    // under the catalog toolbar, a system whose own filters were all cleared still showed fewer
    // articles than it has (review point 2). They are listed in this view's chip row now, set
    // apart as "Aus dem Katalog", and removable right here.
    const [activeFilters, setActiveFilters] = useAtom(filtersAtom);
    // Extra list columns - SHARED with the catalog list (listColumnsAtom), unlike the search and
    // the filters below: which attributes a user wants to see as columns is a display preference,
    // not a selection, and carrying it into a system's Artikelliste is exactly what "die gleiche
    // Möglichkeit" means (see chat). Nothing here feeds back into the tree counts, so there is no
    // reason to keep a second, diverging copy.
    const [listColumns, setListColumns] = useAtom(listColumnsAtom);

    const [documentSearch, setDocumentSearch] = useState('');
    // Which part of the union the Dokumente tab shows (see the component doc comment): everything,
    // only what hangs on the system node itself, or only what comes in via its articles. Mirrors
    // ArticleDetailPage's "Dieser Artikel / Vom System" switch; 'all' keeps the previous behaviour.
    const [documentScope, setDocumentScope] = useState<SystemDocumentScope>('all');
    const [documentLanguage, setDocumentLanguage] = useState<string | null>(null);
    const [documentDocTypes, setDocumentDocTypes] = useState<string[]>([]);
    const [documentPage, setDocumentPage] = useState(1);

    const [articleViewMode, setArticleViewMode] = useState<'grid' | 'list'>('list');
    // 'natural' (not a real sort key - see filteredArticles below) keeps the system's own curated
    // STIBO delivery order (node.articleNumbers) as the default, matching how this tab behaved
    // before column-header sorting was added; clicking any column header overrides it with a real
    // sort.key, same as CatalogPage's own handleColumnSort.
    const [articleSort, setArticleSort] = useState<SortState>({ key: 'natural', dir: 'asc' });
    const [articleColumnFilters, setArticleColumnFilters] = useState<Record<string, string>>({});
    // Merkmalsfilter for THIS system's article list - deliberately local, like the search and the
    // column filters above: writing into the shared filtersAtom would silently change the tree
    // counts and the catalog list behind this view, and would outlive leaving the system.
    const [articleFilters, setArticleFilters] = useState<ActiveFilters>({});
    const [articleFacetsOpen, { open: openArticleFacets, close: closeArticleFacets }] = useDisclosure(false);
    // Column sort of the Dokumente tab (see engine.sortDocumentGroups).
    const [documentSort, setDocumentSort] = useState<DocumentSortState>({ key: 'title', dir: 'asc' });
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // A filter picked for one system means nothing in the next one ("Länge 2,50 m" has no business
    // travelling to the Deckenstützen): everything this view filters by itself - Merkmalsfilter,
    // column filters, the document filters - goes with the system (review point 5: catalog filters
    // survive navigation, a system's own filters do not).
    useEffect(() => {
        setArticleFilters({});
        setArticleColumnFilters({});
        setDocumentSearch('');
        setDocumentLanguage(null);
        setDocumentDocTypes([]);
    }, [nodeId]);

    const labels = useMemo(
        () => labelsQuery.data ?? { language: locale, attributes: {}, lovs: {} },
        [labelsQuery.data, locale]
    );

    const treeIndex = useMemo(() => buildTreeIndex(treeQuery.data ?? []), [treeQuery.data]);
    const node = treeIndex.get(nodeId)?.node;

    const indexByArtNr = useMemo(() => buildIndexByArtNr(indexQuery.data ?? []), [indexQuery.data]);

    // Same Kategorie breadcrumb lookup the catalog's own list view uses (see ProductGrid's
    // ColumnHeader) - needed here too now that Artikelliste sorts/filters by that column as well.
    const categoryLabels = useMemo(
        () => buildCategoryLabels(indexQuery.data ?? [], treeIndex, locale),
        [indexQuery.data, treeIndex, locale]
    );

    // Same source as CatalogPage's own picker: every configured facet is an attribute worth
    // offering as a column.
    const availableColumns: ListColumn[] = useMemo(
        () =>
            (configQuery.data?.facets ?? []).map((f) => ({
                key: f.key,
                label: loc(f.label, locale, f.key),
                unit: f.unit,
            })),
        [configQuery.data, locale]
    );
    const extraColumns = useMemo(
        () => availableColumns.filter((column) => listColumns.includes(column.key)),
        [availableColumns, listColumns]
    );

    // Clicking a column header toggles its direction on repeat clicks, matching CatalogPage's own
    // handleColumnSort.
    const handleArticleSort = (key: string) => {
        setArticleSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    };

    // Every article of the system, and those left after the catalog filters - the difference is
    // what the "N von M durch Katalogfilter ausgeblendet" hint reports.
    const systemArticles = useMemo(
        () =>
            (node?.articleNumbers ?? [])
                .map((artNr) => indexByArtNr.get(artNr))
                .filter((item): item is IndexItem => item !== undefined && item.type === 'product'),
        [indexByArtNr, node]
    );
    const articles = useMemo(() => {
        const config = configQuery.data;
        return config ? applyFilters(systemArticles, activeFilters, byKey(config.facets)) : systemArticles;
    }, [systemArticles, configQuery.data, activeFilters]);
    const hiddenByCatalog = systemArticles.length - articles.length;

    /**
     * Merkmalsfilter offered for THIS system's articles. Computed from the system's own base set -
     * not the whole catalog - so no value shows up that doesn't occur here, and computed BEFORE the
     * filters are applied, with each group's own filter excluded (computeFacets does that; see the
     * project's facet rule).
     *
     * Only facets that actually distinguish something are shown: inside one system, Region,
     * Lifecycle or Datensatztyp are usually the same for every article, and a list of one-value
     * facets is noise rather than a filter (same convention as the document filters, which hide
     * themselves at <= 1 option).
     */
    const articleFacets: ComputedFacet[] = useMemo(() => {
        const config = configQuery.data;
        if (!config) {
            return [];
        }
        return computeFacets(articles, articleFilters, config.facets).filter((facet) => {
            if (facet.type === 'value-list') {
                return (facet.values?.length ?? 0) > 1;
            }
            if (facet.type === 'range') {
                return facet.bounds !== undefined && facet.bounds.min < facet.bounds.max;
            }
            if (facet.type === 'date-range') {
                return facet.dateBounds !== undefined && facet.dateBounds.min < facet.dateBounds.max;
            }
            return Boolean(facet.counts && facet.counts.true > 0 && facet.counts.false > 0);
        });
    }, [articles, articleFilters, configQuery.data]);

    const articleFilterCount = Object.keys(articleFilters).length;

    const filteredArticles = useMemo(() => {
        const config = configQuery.data;
        const byFacets = config ? applyFilters(articles, articleFilters, byKey(config.facets)) : articles;
        const filtered = applyColumnFilters(byFacets, articleColumnFilters, categoryLabels);
        // 'natural' means no column has been clicked yet - `articles` is already in the system's
        // own curated order (see the memo above), so leave it as-is instead of re-sorting.
        return articleSort.key === 'natural' ? filtered : sortItems(filtered, articleSort, categoryLabels);
    }, [articles, articleFilters, articleColumnFilters, articleSort, categoryLabels, configQuery.data]);

    const systemArticleNrs = useMemo(() => new Set(node?.articleNumbers ?? []), [node]);
    // Union of node-direct assignments and this system's own articles' assignments - see the
    // component doc comment above for why documents.json's nodeId rows alone aren't enough.
    //
    // Narrowing, not just filtering: a row carries every target of its document, so keeping the
    // row whole would let the "Verwendung im System" column count articles outside this system.
    // See scopeDocumentsToSystem.
    const scopedDocuments = useMemo(
        () => scopeDocumentsToSystem(documentIndexQuery.data ?? [], nodeId, systemArticleNrs),
        [documentIndexQuery.data, nodeId, systemArticleNrs]
    );
    const totalDocumentCount = useMemo(() => groupMediaByDocument(scopedDocuments).length, [scopedDocuments]);
    // After scopeDocumentsToSystem a row's nodeIds is either [nodeId] or empty, so "direct" is
    // exactly "assigned to this node"; everything else reached the union through an article.
    const directDocuments = useMemo(
        () => scopedDocuments.filter((doc) => (doc.nodeIds ?? []).length > 0),
        [scopedDocuments]
    );
    const articleDocuments = useMemo(
        () => scopedDocuments.filter((doc) => (doc.nodeIds ?? []).length === 0),
        [scopedDocuments]
    );
    const directDocumentCount = useMemo(() => groupMediaByDocument(directDocuments).length, [directDocuments]);
    const articleDocumentCount = useMemo(() => groupMediaByDocument(articleDocuments).length, [articleDocuments]);
    const documentsByScope = { all: scopedDocuments, system: directDocuments, articles: articleDocuments };
    const visibleDocuments = documentsByScope[documentScope];
    const documentUsageIndex = useMemo(
        () => buildDocumentUsageIndex(scopedDocuments, indexByArtNr, treeIndex, locale),
        [scopedDocuments, indexByArtNr, treeIndex, locale]
    );
    const documentResult = useMemo(
        () =>
            selectDocuments(visibleDocuments, indexByArtNr, treeIndex, SEARCH_MIN_CHARS, {
                search: documentSearch,
                language: documentLanguage ?? undefined,
                docTypes: documentDocTypes,
                sort: documentSort,
                locale,
                page: documentPage,
                perPage: DOCUMENTS_PER_PAGE,
            }),
        [
            visibleDocuments,
            indexByArtNr,
            treeIndex,
            documentSearch,
            documentLanguage,
            documentDocTypes,
            documentSort,
            locale,
            documentPage
        ]
    );
    const documentPageCount = Math.ceil(documentResult.total / DOCUMENTS_PER_PAGE);

    const documentDocTypesKey = JSON.stringify(documentDocTypes);
    useEffect(() => {
        setDocumentPage(1);
    }, [documentSearch, documentLanguage, documentDocTypesKey, documentScope]);

    // A language or document type picked in one scope may not exist in the other, which would
    // leave the user staring at an empty table right after switching. Clear them with the scope
    // (same as ArticleDetailPage).
    useEffect(() => {
        setDocumentLanguage(null);
        setDocumentDocTypes([]);
    }, [documentScope]);

    const system = systemQuery.data;
    const title = system ? loc(system.name, locale, system.nodeId) : nodeLabel(node, locale);
    // Plain <img> in the lightbox below - unlike ProductImage it doesn't sign its own URL, so the
    // SAS token for the private asset container has to be added here (see blobAssets.ts). Must stay
    // above the early returns further down: hooks cannot be called conditionally.
    const lightboxImage = useAssetUrl()(system?.image);

    // The system this view is about, as shown at the top of its filter drawers (articles and
    // documents): picture, name, and what a filter set there acts on.
    const systemContext = (hint: string) => (
        <Group
            gap="sm"
            p="sm"
            style={{
                backgroundColor: 'var(--pih-surface)',
                borderLeft: `3px solid ${brand.primary}`,
                borderRadius: 'var(--mantine-radius-sm)'
            }}
            wrap="nowrap"
        >
            <ProductImage
                alt={title}
                fallbackSrc={system?.image}
                height={40}
                src={system?.imageThumbnailUrl}
                width={40}
            />
            <Stack gap={0} style={{ minWidth: 0 }}>
                <Text c="dimmed" fw={600} size="xs">
                    {L.objectTypeSystem}
                </Text>
                <Text fw={700} size="sm" truncate>
                    {title}
                </Text>
                <Text c="dimmed" size="xs">
                    {hint}
                </Text>
            </Stack>
        </Group>
    );

    if (treeQuery.isLoading) {
        return (
            <Center h={240}>
                <Loader />
            </Center>
        );
    }
    if (treeQuery.error) {
        return <Alert color="red">{L.loadError}</Alert>;
    }

    return (
        <Card className="w-full" padding={isMobile ? 'md' : 'xl'} radius="md" withBorder>
            <Stack gap="lg">
                {/* Small preview thumbnail + quick-identification line (object type, nodeId) next
                to the title - mirrors how STIBO STEP itself headers an object (see chat: "In STEP
                sieht das so aktuell aus"), so a scanned page/tab immediately shows what kind of
                object this is, not just its name. Which exact attribute(s) belong on that second
                line is still open (see chat: "das eine oder andere Attribut, müssen wir noch
                definieren") - nodeId is the one stable identifier available today. Explicit
                width=height on the thumbnail, otherwise it stretches to fill this Group's row
                (width defaults to '100%' - fine for the ProductGrid tiles, wrong here where
                nothing else constrains the row) and pushes the title/nodeId text off to the
                right instead of staying a small square (see chat sketch/screenshot). */}
                <Group align="center" gap="md" wrap="nowrap">
                    <ProductImage
                        alt={title}
                        fallbackSrc={system?.image}
                        height={isMobile ? 48 : 60}
                        src={system?.imageThumbnailUrl}
                        width={isMobile ? 48 : 60}
                    />
                    {/* flex:1/minWidth:0 lets this shrink inside the nowrap Group above instead of
                    forcing it wider than the viewport - without it, a flex item's default
                    min-width:auto keeps the Title from wrapping at all, so on mobile the whole
                    Card (and everything below it, incl. the Tabs row) overflows off the right edge
                    instead of the title text just wrapping onto a second line (see chat
                    screenshot). Matches ArticleDetailPage's identical thumbnail+title header. */}
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                        {/* order={1}: the system's name is the top heading of this view - see
                            the heading scale in getCustomThemeOverrides. */}
                        <Title order={1} style={isMobile ? MOBILE_H1_STYLE : undefined}>
                            {title}
                        </Title>
                        {/* Object type only. This line used to append the raw nodeId (STIBO's
                        internal UUID) as "the one stable identifier available today" - but a UUID
                        carries nothing a reader can act on (see chat: "bringt nichts"), and on
                        mobile it ate a whole line of the header. Whichever business attribute
                        belongs here instead is still open on the Fachbereich's side. */}
                        {/* Bold (reported): as a plain dimmed line it read as an afterthought
                            hanging off the title rather than as the object's type. */}
                        <Text c="dimmed" fw={600} size="sm">
                            {L.objectTypeSystem}
                        </Text>
                    </Stack>
                </Group>

                <Tabs defaultValue="info" keepMounted={false}>
                    {/* Mobile legibility (see chat screenshot: the three full labels crammed into a
                    phone width). Two independent measures, because either alone still breaks at
                    some width:
                    - Short labels below `sm` ("Infos"/"Artikel" instead of
                      "Systeminformationen"/"Artikelliste"), swapped purely in CSS so no
                      resize listener/media-query hook is involved.
                    - flex-nowrap + overflow-x-auto on the list: tabs stay on ONE row and scroll
                      sideways instead of wrapping into a ragged two-line block. Mantine's own
                      list wraps by default, which is what produced the squeezed look. */}
                    <Tabs.List className="flex-nowrap overflow-x-auto">
                        <Tabs.Tab className="whitespace-nowrap" value="info">
                            <span className="hidden sm:inline">{L.systemInfoTab}</span>
                            <span className="sm:hidden">{L.systemInfoTabShort}</span>
                        </Tabs.Tab>
                        <Tabs.Tab className="whitespace-nowrap" value="articles">
                            <span className="hidden sm:inline">{L.systemArticlesTab}</span>
                            <span className="sm:hidden">{L.systemArticlesTabShort}</span> ({articles.length})
                        </Tabs.Tab>
                        <Tabs.Tab className="whitespace-nowrap" value="documents">
                            {L.systemDocumentsTab} ({totalDocumentCount})
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel pt="md" value="info">
                        {systemQuery.isLoading ? (
                            <Center h={120}>
                                <Loader size="sm" />
                            </Center>
                        ) : system ? (
                            <Group align="flex-start" gap="xl" wrap="wrap">
                                {system.image && (
                                    <button
                                        aria-label={L.enlargeImage}
                                        style={{
                                            background: 'none',
                                            border: 0,
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            padding: 0,
                                            width: 280,
                                        }}
                                        type="button"
                                        onClick={() => setLightboxOpen(true)}
                                    >
                                        <ProductImage
                                            alt={title}
                                            fallbackSrc={system.image}
                                            height={280}
                                            src={system.imageThumbnailUrl}
                                        />
                                    </button>
                                )}
                                <Stack gap="md" style={{ flex: 1, minWidth: 280 }}>
                                    {loc(system.description, locale) && (
                                        <Text component="div">
                                            <RichText value={loc(system.description, locale)} />
                                        </Text>
                                    )}
                                    {(system.lifecycleGroup || system.subsidiary) && (
                                        <Stack gap={4}>
                                            {system.lifecycleGroup && (
                                                <Text c="dimmed" size="sm">
                                                    {L.category}: {system.lifecycleGroup}
                                                </Text>
                                            )}
                                            {system.subsidiary && (
                                                <Text c="dimmed" size="sm">
                                                    {L.manufacturer}: {system.subsidiary}
                                                </Text>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                            </Group>
                        ) : (
                            <Text c="dimmed">{L.noSystemInfo}</Text>
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel pt="md" value="articles">
                        {indexQuery.isLoading ? (
                            <Center h={120}>
                                <Loader size="sm" />
                            </Center>
                        ) : articles.length === 0 ? (
                            <Text c="dimmed">{L.noSystemArticles}</Text>
                        ) : (
                            <Stack gap="sm">
                                <Group justify="space-between" wrap="wrap">
                                    {/* No search field of its own (review point 3): the toolbar's search
                                        is the one search - it covers the whole catalog and shows
                                        its hits in place of this view. Within this list the
                                        column filters (header funnels) narrow by name or number.
                                        The count says what is left. */}
                                    <Group gap="sm" wrap="nowrap">
                                        <Text c="dimmed" size="sm">
                                            {filteredArticles.length} / {articles.length}
                                        </Text>
                                    </Group>
                                    {/* The three list controls stay one block on the right, in the
                                        same order as the catalog toolbar (view, columns, filter) -
                                        `justify="space-between"` on the row above would otherwise
                                        have spread them individually across the whole width. */}
                                    <Group gap="xs" wrap="nowrap">
                                        <ViewModeToggle value={articleViewMode} onChange={setArticleViewMode} />
                                        {articleViewMode === 'list' && (
                                            <ColumnPicker
                                                available={availableColumns}
                                                value={listColumns}
                                                onChange={setListColumns}
                                            />
                                        )}
                                        {/* Same affordance as the catalog toolbar: icon with a count
                                            badge, opening the same FacetsPanel in a drawer. Hidden
                                            when nothing in this system actually differs (see
                                            articleFacets). */}
                                        {articleFacets.length > 0 && (
                                            <ActionIcon
                                                aria-label={L.articleFilterTitle}
                                                aria-pressed={articleFilterCount > 0}
                                                title={L.articleFilterTitle}
                                                variant={articleFilterCount ? 'filled' : 'default'}
                                                onClick={openArticleFacets}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                                                    tune
                                                </span>
                                            </ActionIcon>
                                        )}
                                    </Group>
                                </Group>
                                {/* This list's own Merkmalsfilter as removable chips - same look as
                                    the catalog toolbar's row, but captioned with the system's name:
                                    they live HERE, in the system, not in the catalog (reported).
                                    The catalog-wide filters that also narrow this list are shown
                                    up there, under the toolbar. */}
                                <ActiveFilterChips
                                    L={L}
                                    chips={[
                                        ...facetFilterChips(
                                            articleFilters,
                                            configQuery.data?.facets ?? [],
                                            labels,
                                            locale,
                                            L,
                                            setArticleFilters
                                        ),
                                        // The table's own column filters (header funnels) too.
                                        ...columnFilterChips(
                                            articleColumnFilters,
                                            (key) =>
                                                key === 'name'
                                                    ? L.name
                                                    : key === 'artNr'
                                                      ? L.articleNumber
                                                      : (availableColumns.find((column) => column.key === key)?.label ??
                                                        key),
                                            [],
                                            L,
                                            setArticleColumnFilters
                                        ),
                                    ]}
                                    label={L.systemFilterChipsLabel.replace('{name}', title)}
                                    onClearAll={() => {
                                        setArticleFilters({});
                                        setArticleColumnFilters({});
                                    }}
                                />
                                {/* The catalog filters acting on this list too (review point 2) - outlined
                                    rather than filled, so they read as coming from elsewhere, and
                                    removable here (which removes them from the catalog as well). */}
                                <ActiveFilterChips
                                    L={L}
                                    chips={filterChipsWithPresets(
                                        activeFilters,
                                        configQuery.data?.facets ?? [],
                                        configQuery.data?.presets ?? [],
                                        labels,
                                        locale,
                                        L,
                                        setActiveFilters
                                    )}
                                    label={L.catalogFiltersLabel}
                                    variant="outline"
                                />
                                {hiddenByCatalog > 0 && (
                                    <Text c="dimmed" size="xs">
                                        {L.hiddenByCatalogFilters
                                            .replace('{hidden}', String(hiddenByCatalog))
                                            .replace('{total}', String(systemArticles.length))}
                                    </Text>
                                )}
                                {filteredArticles.length === 0 ? (
                                    <Text c="dimmed">{L.noResults}</Text>
                                ) : (
                                    <ProductGrid
                                        // No System/Kategorie column here: every article in this
                                        // list belongs to the very system named in the page header
                                        // right above it, so the column repeated one value down
                                        // the whole table (see chat: "wenn wir in einem System die
                                        // Artikel anzeigen, lassen wir die Spalte 'System' weg da
                                        // nicht relevant"). Where an article ALSO belongs to other
                                        // systems, its own Verwendung tab lists them.
                                        categoryDisplay="none"
                                        categoryLabels={categoryLabels}
                                        columnFilters={articleColumnFilters}
                                        columns={extraColumns}
                                        items={filteredArticles}
                                        sort={articleSort}
                                        viewMode={articleViewMode}
                                        onColumnFilter={(key, value) =>
                                            setArticleColumnFilters((prev) => ({ ...prev, [key]: value }))
                                        }
                                        onSort={handleArticleSort}
                                    />
                                )}
                            </Stack>
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel pt="md" value="documents">
                        {documentIndexQuery.isLoading ? (
                            <Center h={120}>
                                <Loader size="sm" />
                            </Center>
                        ) : totalDocumentCount === 0 ? (
                            <Text c="dimmed">{L.noSystemDocuments}</Text>
                        ) : (
                            <Stack gap="sm">
                                {/* Only worth showing when both parts are non-empty - otherwise it would
                                    be a switch with one usable position. The counts are LOGICAL documents
                                    (language variants collapsed), the unit the tab badge uses too. */}
                                {directDocumentCount > 0 && articleDocumentCount > 0 && (
                                    <SegmentedControl
                                        data={[
                                            { value: 'all', label: `${L.systemDocumentsAll} (${totalDocumentCount})` },
                                            {
                                                value: 'system',
                                                label: `${L.systemDocumentsDirect} (${directDocumentCount})`
                                            },
                                            {
                                                value: 'articles',
                                                label: `${L.systemDocumentsFromArticles} (${articleDocumentCount})`
                                            }
                                        ]}
                                        value={documentScope}
                                        fullWidth
                                        onChange={(value) => setDocumentScope(value as SystemDocumentScope)}
                                    />
                                )}
                                {documentScope === 'articles' && (
                                    <Text c="dimmed" size="sm">
                                        {L.systemDocumentsFromArticlesHint}
                                    </Text>
                                )}
                                <DocumentFilterBar
                                    chipsLabel={L.systemFilterChipsLabel.replace('{name}', title)}
                                    context={systemContext(L.systemDocumentFilterHint)}
                                    docTypes={documentDocTypes}
                                    documents={visibleDocuments}
                                    indexByArtNr={indexByArtNr}
                                    language={documentLanguage}
                                    leading={
                                        <TextInput
                                            placeholder={L.documentSearchPlaceholder}
                                            value={documentSearch}
                                            onChange={(event) => setDocumentSearch(event.currentTarget.value)}
                                        />
                                    }
                                    search={documentSearch}
                                    treeIndex={treeIndex}
                                    onDocTypesChange={setDocumentDocTypes}
                                    onLanguageChange={setDocumentLanguage}
                                />
                                <DocumentSearchResults
                                    items={documentResult.items}
                                    locale={locale}
                                    sort={documentSort}
                                    systemNodeId={nodeId}
                                    usageIndex={documentUsageIndex}
                                    onSelectNode={onNavigateToNode}
                                    onSort={(key: DocumentSortKey) =>
                                        setDocumentSort((prev) =>
                                            prev.key === key
                                                ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                                : { key, dir: 'asc' }
                                        )
                                    }
                                />
                                {documentPageCount > 1 && (
                                    <Group justify="flex-end">
                                        <Pagination total={documentPageCount} value={documentPage} onChange={setDocumentPage} />
                                    </Group>
                                )}
                            </Stack>
                        )}
                    </Tabs.Panel>
                </Tabs>
            </Stack>

            {/* Merkmalsfilter for the Artikelliste - same panel and same right-hand drawer the
                catalog uses, just fed with this system's own facets and its own local filter state. */}
            {/* Its own heading ("Artikel filtern", not the catalog's "Filter") and the system it acts on
                right under it, with the system's picture: the drawer otherwise looked exactly like
                the catalog's and nobody could tell that a filter set here stays in this system
                (reported: "dass ich mich im System-Dialog befinde ... noch etwas verwirrend"). */}
            <Drawer
                opened={articleFacetsOpen}
                padding="lg"
                position="right"
                title={`${L.articleFilterTitle} - ${title}`}
                onClose={closeArticleFacets}
                {...FILTER_DRAWER_HEADER_PROPS}
            >
                <FilterDrawerHeader
                    canReset={Object.keys(articleFilters).length > 0}
                    context={systemContext(L.systemFilterHint.replace('{count}', String(articles.length)))}
                    title={L.articleFilterTitle}
                    onClose={closeArticleFacets}
                    onReset={() => setArticleFilters({})}
                />
                <FacetsPanel
                    facets={articleFacets}
                    filters={articleFilters}
                    labels={labels}
                    locale={locale}
                    onChange={setArticleFilters}
                />
                {/* Says what the filters add up to and takes the user back to the list - on a phone
                    the drawer covers the whole screen, so the list itself is out of sight. */}
                <div
                    style={{
                        position: 'sticky',
                        bottom: 'calc(-1 * var(--mantine-spacing-lg))',
                        marginTop: 'var(--mantine-spacing-lg)',
                        paddingBlock: 'var(--mantine-spacing-sm)',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button fullWidth onClick={closeArticleFacets}>
                        {L.showArticles.replace('{count}', String(filteredArticles.length))}
                    </Button>
                </div>
            </Drawer>

            {lightboxImage && (
                <Modal
                    opened={lightboxOpen}
                    padding="md"
                    size="auto"
                    title={title}
                    centered
                    onClose={() => setLightboxOpen(false)}
                >
                    <img
                        alt={title}
                        src={lightboxImage}
                        style={{ display: 'block', maxHeight: '80vh', maxWidth: '80vw', objectFit: 'contain' }}
                    />
                </Modal>
            )}
        </Card>
    );
};
